import { Link } from "react-router-dom";
import { Shield, Headphones, RefreshCw } from "lucide-react";
import logo from "@/assets/logo.webp";

const footerLinks = [
  {
    title: "קישורים מהירים",
    links: [
      { label: "כניסה לצוות", href: "https://app.onestep.co.il/login" },
      { label: "כניסה לדיירים", href: "https://app.onestep.co.il/portal" },
      { label: "בלוג ומאמרים", href: "/blog" },
    ],
  },
  {
    title: "פתרונות תוכנה",
    links: [
      { label: "תוכנה לפינוי בינוי", href: "/software/pinui-binui" },
      { label: "מערכת להתחדשות עירונית", href: "/software/hitchadshut-ironit" },
      { label: "ניהול פרויקטים בנדלן", href: "/software/project-management" },
      { label: "מערכת לניהול דיירים", href: "/software/tenant-management" },
      { label: "CRM ליזמי נדלן", href: "/software/developer-crm" },
    ],
  },
  {
    title: "פינוי בינוי לפי עיר",
    links: [
      { label: "פינוי בינוי בתל אביב", href: "/cities/pinui-binui-tel-aviv" },
      { label: "פינוי בינוי בבאר שבע", href: "/cities/pinui-binui-beer-sheva" },
      { label: "פינוי בינוי בחיפה", href: "/cities/pinui-binui-haifa" },
    ],
  },
  {
    title: "מידע משפטי",
    links: [
      { label: "תנאי שימוש", href: "/terms" },
      { label: "מדיניות פרטיות", href: "/privacy" },
      { label: "הצהרת נגישות", href: "/accessibility" },
      { label: "מפת האתר", href: "/sitemap" },
      { label: "llms.txt", href: "/llms.txt", external: true },
    ],
  },
  {
    title: "צור קשר",
    links: [
      { label: "info@onestep.co.il", href: "mailto:info@onestep.co.il" },
      { label: "050-8558-400", href: "tel:0508558400" },
    ],
  },
  {
    title: "שעות פעילות",
    links: [
      { label: "ראשון–חמישי: 09:00–20:00", href: "" },
      { label: "שישי וערבי חג: 10:00–14:00", href: "" },
      { label: "שבתות וחגים: סגור", href: "" },
    ],
  },
];

const badges = [
  { icon: Shield, label: "אבטחת מידע מתקדמת" },
  { icon: Headphones, label: "תמיכה מקצועית" },
  { icon: RefreshCw, label: "עדכונים שוטפים" },
];

const Footer = () => {
  return (
    <footer className="border-t border-border bg-primary text-primary-foreground">
      {/* Trust badges */}
      <div className="border-b border-primary-foreground/10">
        <div className="container py-6">
          <p className="text-center text-sm text-primary-foreground/60 mb-4">
            בואו ותצטרפו לחברות הגדולות
          </p>
          <div className="flex justify-center gap-8 flex-wrap">
            {badges.map((b) => (
              <div key={b.label} className="flex items-center gap-2 text-primary-foreground/50">
                <b.icon size={16} />
                <span className="text-xs font-medium">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <img src={logo} alt="OneStep" className="h-24 mb-4 brightness-0 invert object-contain" width="1780" height="940" loading="lazy" decoding="async" />
            <p className="text-sm text-primary-foreground/70 leading-relaxed max-w-xs">
              OneStep, לבנות חכם מהיסוד.
            </p>
            <p className="text-xs text-primary-foreground/50 mt-2 leading-relaxed">
              הסטנדרט החדש שמשנה את כללי המשחק בעולם ההתחדשות העירונית.
            </p>
            <h3 className="font-semibold mb-3 text-sm mt-5">עקבו אחרינו</h3>
            <div className="flex items-center gap-3">
              <a
                href="https://www.youtube.com/@OneStep_crm"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="ערוץ היוטיוב של OneStep"
                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-foreground/10 hover:bg-[#FF0000]/20 transition-colors group"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" className="fill-[#FF0000]">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.546 12 3.546 12 3.546s-7.505 0-9.377.504A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.504 9.376.504 9.376.504s7.505 0 9.377-.504a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a
                href="https://www.tiktok.com/@onestep.co.il"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="עמוד הטיקטוק של OneStep"
                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-foreground/10 hover:bg-[#010101]/20 transition-colors group"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" className="fill-primary-foreground">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.51a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.87a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.3z"/>
                </svg>
              </a>
              <a
                href="https://share.google/ipeIOgacnyHzZBdWE"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="הפרופיל של OneStep בגוגל"
                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-foreground/10 hover:bg-[#4285F4]/20 transition-colors group"
              >
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </a>
              <a
                href="https://www.facebook.com/onestep.co.il"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="עמוד הפייסבוק של OneStep"
                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-foreground/10 hover:bg-[#1877F2]/20 transition-colors group"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" className="fill-[#1877F2]">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/onestep.co.il/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="עמוד האינסטגרם של OneStep"
                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-foreground/10 hover:bg-[#E4405F]/20 transition-colors group"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" className="fill-[#E4405F]">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Links */}
          {footerLinks.map((group) => (
            <div key={group.title}>
              <h3 className="font-semibold mb-4 text-sm">{group.title}</h3>
              <ul className="space-y-2.5">
                {group.links.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith("http") || link.href.startsWith("mailto") || link.href.startsWith("tel") || (link as any).external ? (
                      <a
                        href={link.href}
                        rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                        className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : link.href === "" ? (
                      <span className="text-sm text-primary-foreground/60">
                        {link.label}
                      </span>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-primary-foreground/10 text-center">
          <p className="text-xs text-primary-foreground/50">
            © 2026 OneStep. כל הזכויות שמורות.
          </p>
          <p className="text-xs text-primary-foreground/60 mt-1">
            הצעד הראשון בפינוי בינוי
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
